import { apiFetch, readErrorMessage, readJson } from '../../api/client';
import { PATIENT_TRACKING_API_BASE } from '../patientTrackingTypes';
import type {
  PatientTrackingDiscussionMessage,
  PatientTrackingDiscussionTargetType,
  PatientTrackingDiscussionThreadDto,
  PtMentionCandidatesResponse
} from './patientTrackingDiscussionTypes';

function camelizeKeys<T>(value: unknown): T {
  if (value == null || typeof value !== 'object') return value as T;
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    out[key.length > 0 ? key[0].toLowerCase() + key.slice(1) : key] = val;
  }
  return out as T;
}

function normalizeDiscussionMessage(raw: unknown): PatientTrackingDiscussionMessage {
  const m = camelizeKeys<Record<string, unknown>>(raw);
  return {
    id: String(m.id ?? ''),
    text: String(m.text ?? ''),
    submittedBy: m.submittedBy != null ? String(m.submittedBy) : null,
    submittedByUserId: m.submittedByUserId != null ? String(m.submittedByUserId) : null,
    submittedByDisplayName:
      m.submittedByDisplayName != null ? String(m.submittedByDisplayName) : null,
    submittedAt: String(m.submittedAt ?? ''),
    updatedAt: m.updatedAt != null ? String(m.updatedAt) : null,
    isDeleted: Boolean(m.isDeleted)
  };
}

function normalizeDiscussionThread(raw: unknown): PatientTrackingDiscussionThreadDto {
  const t = camelizeKeys<Record<string, unknown>>(raw);
  const rawMessages = t.messages ?? t.Messages;
  const messages = Array.isArray(rawMessages)
    ? rawMessages.map((m) => normalizeDiscussionMessage(m))
    : [];
  return {
    threadId: String(t.threadId ?? ''),
    targetType: String(t.targetType ?? '') as PatientTrackingDiscussionTargetType,
    targetId: String(t.targetId ?? ''),
    messages
  };
}

function discussionBase(
  apiBase: string,
  targetType: PatientTrackingDiscussionTargetType,
  targetId: string
): string {
  return `${apiBase}/discussions/${encodeURIComponent(targetType)}/${encodeURIComponent(targetId)}`;
}

async function ok<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<T>(res);
}

export async function fetchPatientTrackingDiscussionThread(
  targetType: PatientTrackingDiscussionTargetType,
  targetId: string,
  apiBase = PATIENT_TRACKING_API_BASE
): Promise<PatientTrackingDiscussionThreadDto> {
  const res = await apiFetch(`${discussionBase(apiBase, targetType, targetId)}/thread`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return normalizeDiscussionThread(await readJson(res));
}

export async function fetchPatientTrackingMentionCandidates(
  targetType: PatientTrackingDiscussionTargetType,
  targetId: string,
  query: string,
  apiBase = PATIENT_TRACKING_API_BASE
): Promise<PtMentionCandidatesResponse> {
  const params = new URLSearchParams();
  if (query.trim()) params.set('q', query.trim());
  const qs = params.toString();
  const res = await apiFetch(
    `${discussionBase(apiBase, targetType, targetId)}/mention-candidates${qs ? `?${qs}` : ''}`
  );
  return ok<PtMentionCandidatesResponse>(res);
}

export async function createPatientTrackingDiscussionMessage(
  targetType: PatientTrackingDiscussionTargetType,
  targetId: string,
  text: string,
  apiBase = PATIENT_TRACKING_API_BASE
): Promise<{ id: string }> {
  const res = await apiFetch(`${discussionBase(apiBase, targetType, targetId)}/messages`, {
    method: 'POST',
    body: JSON.stringify({ text })
  });
  return ok<{ id: string }>(res);
}

export async function updatePatientTrackingDiscussionMessage(
  targetType: PatientTrackingDiscussionTargetType,
  targetId: string,
  messageId: string,
  text: string,
  apiBase = PATIENT_TRACKING_API_BASE
): Promise<void> {
  const res = await apiFetch(
    `${discussionBase(apiBase, targetType, targetId)}/messages/${encodeURIComponent(messageId)}`,
    { method: 'PATCH', body: JSON.stringify({ text }) }
  );
  if (!res.ok) throw new Error(await readErrorMessage(res));
}

export async function deletePatientTrackingDiscussionMessage(
  targetType: PatientTrackingDiscussionTargetType,
  targetId: string,
  messageId: string,
  apiBase = PATIENT_TRACKING_API_BASE
): Promise<void> {
  const res = await apiFetch(
    `${discussionBase(apiBase, targetType, targetId)}/messages/${encodeURIComponent(messageId)}`,
    { method: 'DELETE' }
  );
  if (!res.ok) throw new Error(await readErrorMessage(res));
}
